/**
 * <heritage-timeline>
 *
 * Progressive enhancement for the Heritage & Science Timeline section.
 * The section is fully readable and scrollable without this file — everything
 * here adds polish on top of native overflow scrolling.
 *
 * Deliberately has no @theme/* imports so the section stays portable if the
 * theme base changes.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class HeritageTimeline extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-heritage-track]');
    if (!this.track) return;

    this.items = Array.from(this.querySelectorAll('[data-heritage-item]'));
    this.railFill = this.querySelector('[data-heritage-rail-fill]');
    this.prevButton = this.querySelector('[data-heritage-prev]');
    this.nextButton = this.querySelector('[data-heritage-next]');

    this.onScroll = this.onScroll.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('keydown', this.onKeydown);
    this.prevButton?.addEventListener('click', () => this.step(-1));
    this.nextButton?.addEventListener('click', () => this.step(1));

    this.observeReveal();
    this.updateProgress();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('keydown', this.onKeydown);
    this.revealObserver?.disconnect();
  }

  /**
   * Fades milestones in as they enter view. With reduced motion requested we
   * mark everything visible immediately rather than animating.
   */
  observeReveal() {
    if (REDUCED_MOTION.matches || !('IntersectionObserver' in window)) return;

    // Opt into the hidden-then-revealed state only now that we know we can
    // actually reveal. Without this attribute the CSS leaves milestones visible,
    // so a script that never loads can't blank the section.
    this.setAttribute('data-reveal', '');

    this.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          this.revealObserver.unobserve(entry.target);
        });
      },
      { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.15 }
    );

    this.items.forEach((item) => this.revealObserver.observe(item));
  }

  onScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.updateProgress();
    });
  }

  /**
   * Arrow keys move between milestones when the track itself has focus, so the
   * timeline is navigable without a pointer.
   */
  onKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.step(1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.step(-1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.scrollTo(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.scrollTo(this.track.scrollWidth);
    }
  }

  step(direction) {
    const item = this.items[0];
    if (!item) return;
    const stride = item.getBoundingClientRect().width || this.track.clientWidth * 0.8;
    this.scrollTo(this.track.scrollLeft + stride * direction);
  }

  scrollTo(left) {
    this.track.scrollTo({
      left,
      behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
    });
  }

  updateProgress() {
    const max = this.track.scrollWidth - this.track.clientWidth;

    if (this.railFill) {
      // A single-screen track has nothing to progress through; show it complete.
      const ratio = max > 0 ? this.track.scrollLeft / max : 1;
      this.railFill.style.inlineSize = `${Math.min(Math.max(ratio, 0), 1) * 100}%`;
    }

    if (this.prevButton) {
      this.prevButton.disabled = this.track.scrollLeft <= 1;
    }

    if (this.nextButton) {
      this.nextButton.disabled = max <= 0 || this.track.scrollLeft >= max - 1;
    }
  }
}

if (!customElements.get('heritage-timeline')) {
  customElements.define('heritage-timeline', HeritageTimeline);
}
